import React from 'react';
import { Badge } from '../../ui/badge';
import { Mail, Phone, MapPin, Globe, Linkedin, Github, Calendar } from 'lucide-react';

// Local types aligned with ResumePreview
interface PersonalInfo {
  fullName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  linkedin?: string;
  website?: string;
  github?: string;
}

interface Education {
  id: string;
  institution: string;
  degree: string;
  fieldOfStudy: string;
  startDate: string;
  endDate: string;
  isCurrentlyStudying: boolean;
  gpa?: string;
  description?: string;
  location?: string;
}

interface Experience {
  id: string;
  jobTitle: string;
  company: string;
  location: string;
  startDate: string;
  endDate: string;
  isCurrentJob: boolean;
  description: string;
  achievements: string[];
}

interface Skill {
  id: string;
  name: string;
  category: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
}

interface Project {
  id: string;
  name: string;
  description: string;
  technologies: string[];
  startDate: string;
  endDate: string;
  projectUrl?: string;
  githubUrl?: string;
  highlights: string[];
}

interface Language {
  id?: string;
  name: string;
  proficiency?: string;
}

interface ResumeData {
  title: string;
  personalInfo: PersonalInfo;
  summary: string;
  education: Education[];
  experience: Experience[];
  skills: Skill[];
  languages?: Language[];
  projects: Project[];
  template: string;
}

interface ProfessionalTemplateProps {
  data: ResumeData;
}

const ProfessionalTemplate: React.FC<ProfessionalTemplateProps> = ({ data }) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short'
    });
  };

  const formatDateRange = (startDate: string, endDate: string, isCurrent: boolean) => {
    const start = formatDate(startDate);
    const end = isCurrent ? 'Present' : formatDate(endDate);
    return `${start} - ${end}`;
  };

  const fullAddress = () => {
    const { address, city, state, zipCode, country } = data.personalInfo;
    return [address, city, state, zipCode, country].filter(Boolean).join(', ');
  };

  const groupedSkills = data.skills.reduce((acc, skill) => {
    (acc[skill.category] = acc[skill.category] || []).push(skill);
    return acc;
  }, {} as Record<string, Skill[]>);

  return (
    <div className="bg-white max-w-4xl mx-auto relative" style={{ minHeight: 'auto', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Top Header: Name + Title */}
      <div className="border-b border-gray-200 px-8 py-6">
        <h1 className="text-4xl font-bold text-gray-900">{data.personalInfo.fullName}</h1>
        {data.title && (
          <p className="text-gray-600 text-lg mt-1">{data.title}</p>
        )}
      </div>

      {/* Body with Sidebar layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
        {/* Sidebar */}
        <aside className="bg-gray-50 border-r border-gray-200 p-6 md:p-8">
          {/* Contact */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold tracking-widest text-gray-900 uppercase">Contact</h2>
            <div className="space-y-2 text-sm text-gray-900">
              {data.personalInfo.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-700" />
                  <span className="truncate">{data.personalInfo.email}</span>
                </div>
              )}
              {data.personalInfo.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-700" />
                  <span>{data.personalInfo.phone}</span>
                </div>
              )}
              {fullAddress() && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-gray-700 mt-0.5" />
                  <span className="leading-snug">{fullAddress()}</span>
                </div>
              )}
              {data.personalInfo.linkedin && (
                <div className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4 text-gray-700" />
                  <span className="truncate">{data.personalInfo.linkedin}</span>
                </div>
              )}
              {data.personalInfo.website && (
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-gray-700" />
                  <span className="truncate">{data.personalInfo.website}</span>
                </div>
              )}
              {data.personalInfo.github && (
                <div className="flex items-center gap-2">
                  <Github className="h-4 w-4 text-gray-700" />
                  <span className="truncate">{data.personalInfo.github}</span>
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="my-6 h-px bg-gray-200" />

          {/* Skills */}
          {data.skills.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold tracking-widest text-gray-700 uppercase">Skills</h2>
              <div className="space-y-4">
                {Object.entries(groupedSkills).map(([category, skills]) => (
                  <div key={category}>
                    <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">{category}</h3>
                    <div className="flex flex-wrap gap-2">
                      {skills.map((skill) => (
                        <span
                          key={skill.id}
                          className="inline-block max-w-full break-words whitespace-normal text-xs border border-gray-300 text-gray-700 rounded px-2 py-1 bg-white"
                        >
                          {skill.name}
                          {(skill.level || (skill as any).proficiency) && (
                            <span className="ml-1 text-[10px] text-gray-500">({String(skill.level || (skill as any).proficiency)})</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="my-6 h-px bg-gray-200" />

          {/* Languages */}
          {data.languages && data.languages.filter(l => l.name && l.name.trim()).length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold tracking-widest text-gray-700 uppercase">Languages</h2>
              <div className="flex flex-wrap gap-2">
                {data.languages.filter(l => l.name && l.name.trim()).map((lang, idx) => (
                  <span
                    key={idx}
                    className="inline-block max-w-full break-words whitespace-normal text-xs border border-gray-300 text-gray-700 rounded px-2 py-1 bg-white"
                  >
                    {lang.name}
                    {lang.proficiency && (
                      <span className="ml-1 text-[10px] text-gray-500">({lang.proficiency})</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Education (compact) */}
          {data.education.length > 0 && data.education[0].institution && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold tracking-widest text-gray-700 uppercase">Education</h2>
              <div className="space-y-3 text-sm">
                {data.education.map((edu) => (
                  <div key={edu.id}>
                    <div className="font-medium text-gray-900">{edu.institution}</div>
                    <div className="text-gray-700">{edu.degree}</div>
                    {edu.fieldOfStudy && (
                      <div className="text-gray-600">{edu.fieldOfStudy}</div>
                    )}
                    <div className="text-gray-500">
                      {formatDateRange(edu.startDate, edu.endDate, edu.isCurrentlyStudying)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="md:col-span-2 p-6 md:p-8 space-y-8">
          {/* Summary */}
          {data.summary && (
            <section>
              <h2 className="text-sm font-semibold tracking-widest text-gray-700 uppercase">Profile</h2>
              <p className="mt-2 text-gray-800 leading-relaxed">
                {data.summary}
              </p>
            </section>
          )}

          {/* Experience */}
          {data.experience.length > 0 && data.experience[0].jobTitle && (
            <section>
              <h2 className="text-sm font-semibold tracking-widest text-gray-700 uppercase">Experience</h2>
              <div className="mt-4 space-y-6">
                {data.experience.map((exp) => (
                  <div key={exp.id} className="border border-gray-200 rounded-md p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{exp.jobTitle}</h3>
                        <div className="text-blue-700 font-medium">{exp.company}</div>
                        {exp.location && (
                          <div className="text-gray-600 flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {exp.location}
                          </div>
                        )}
                      </div>
                      <div className="text-gray-600 text-sm flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDateRange(exp.startDate, exp.endDate, exp.isCurrentJob)}
                      </div>
                    </div>
                    {exp.description && (
                      <p className="text-gray-700 mt-2">{exp.description}</p>
                    )}
                    {exp.achievements.length > 0 && exp.achievements[0] && (
                      <ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
                        {exp.achievements.filter(a => a.trim()).map((a, i) => (
                          <li key={i}>{a}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Projects */}
          {data.projects.length > 0 && data.projects[0].name && (
            <section>
              <h2 className="text-sm font-semibold tracking-widest text-gray-700 uppercase">Projects</h2>
              <div className="mt-4 space-y-6">
                {data.projects.map((project) => (
                  <div key={project.id} className="border border-gray-200 rounded-md p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                        {project.description && (
                          <p className="text-gray-700 mt-1">{project.description}</p>
                        )}
                        {project.technologies.length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm font-medium text-gray-600">Technologies: </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {project.technologies.map((tech, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {tech}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {project.highlights.length > 0 && project.highlights[0] && (
                          <ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
                            {project.highlights.filter(h => h.trim()).map((h, i) => (
                              <li key={i}>{h}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="text-gray-600 text-sm flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDateRange(project.startDate, project.endDate, false)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
      <div className="mt-4 text-center text-sm italic pt-2 border-t text-[#e86f2d] print:mt-4">This CV Generated From MegajobNepal</div>
    </div>
  );
};

export default ProfessionalTemplate;
